/**
 * Where a pocket sits beside a task on a board (spec §6.5): next to the
 * task's column or day cell, on the side with more room, or at the frame's
 * right edge in the List view and in an opened board, whose strip keeps that
 * lane free (`TASK_POCKET_LANE`); top-aligned with the task and kept inside the
 * frame's height. Positions are the card's own (unscaled) pixels, because
 * the pocket lives in the board's node layer and moves with the canvas.
 */
export const TASK_POCKET_WIDTH = 390;
/** Below this canvas zoom the pocket docks, so its text never shrinks further. */
export const MIN_ANCHORED_ZOOM = 0.75;

/** The docked pocket's distance from the right edge (`right-3` below). */
export const TASK_POCKET_DOCK_INSET = 12;

/** Shared right-edge position for pockets outside the canvas transform. */
export function pocketDockClassName({
  headerChrome,
}: {
  headerChrome: boolean;
}): string {
  return `pointer-events-none absolute bottom-6 right-3 flex flex-col items-end ${headerChrome ? "top-[60px]" : "top-4"}`;
}

const GAP = 8;
const INSET = 12;
/**
 * The lane an opened board keeps at its right edge for the pocket: the
 * pocket, its inset from the edge and a gap before the last column.
 */
export const TASK_POCKET_LANE = TASK_POCKET_WIDTH + INSET + GAP;
/** The pocket's padding and header: its title then lines up with the task. */
const TASK_TITLE_OFFSET = 44;

export interface PocketPlacementInput {
  frame: { width: number; height: number };
  item: { top: number } | null;
  lane: { left: number; width: number } | null;
  size: { width: number; height: number };
  titleOffset: number;
  /** The board is opened: the pocket takes its right-edge lane. */
  opened: boolean;
}

export function pocketPlacement({
  frame,
  item,
  lane,
  size,
  titleOffset,
  opened,
}: PocketPlacementInput): { left: number; top: number } {
  const left =
    lane && !opened
      ? lane.left + lane.width / 2 < frame.width / 2
        ? lane.left + lane.width + GAP
        : lane.left - size.width - GAP
      : frame.width - size.width - INSET;
  const maxTop = Math.max(INSET, frame.height - size.height - INSET);
  const wanted = (item?.top ?? INSET + titleOffset) - titleOffset;
  return { left, top: Math.min(Math.max(wanted, INSET), maxTop) };
}

function findTaskPocketItem(
  card: HTMLElement,
  taskId: string,
): HTMLElement | null {
  for (const element of card.querySelectorAll<HTMLElement>(
    "[data-task-pocket-item]",
  )) {
    if ((element.dataset.taskPocketItem ?? "").split(" ").includes(taskId)) {
      return element;
    }
  }
  return null;
}

/** The task pocket's position in the board card, measured from the DOM. */
export function measureTaskPocketPosition(
  card: HTMLElement,
  taskId: string,
  pocketHeight: number,
  opened: boolean,
): { left: number; top: number } | null {
  const cardRect = card.getBoundingClientRect();
  const scale = card.offsetWidth > 0 ? cardRect.width / card.offsetWidth : 1;
  const local = (rect: DOMRect) => ({
    left: (rect.left - cardRect.left) / scale,
    top: (rect.top - cardRect.top) / scale,
    width: rect.width / scale,
  });
  const item = findTaskPocketItem(card, taskId);
  if (!item) return null;
  const itemRect = item.getBoundingClientRect();
  if (
    itemRect.width <= 0 ||
    itemRect.height <= 0 ||
    itemRect.bottom <= cardRect.top ||
    itemRect.top >= cardRect.bottom ||
    itemRect.right <= cardRect.left ||
    itemRect.left >= cardRect.right
  )
    return null;
  const lane = item?.closest<HTMLElement>("[data-task-pocket-lane]");
  const position = pocketPlacement({
    frame: { width: card.offsetWidth, height: card.offsetHeight },
    item: item ? local(item.getBoundingClientRect()) : null,
    lane:
      lane && card.contains(lane) ? local(lane.getBoundingClientRect()) : null,
    size: { width: TASK_POCKET_WIDTH, height: pocketHeight },
    titleOffset: TASK_TITLE_OFFSET,
    opened,
  });
  const minLeft = (INSET - cardRect.left) / scale;
  const maxLeft =
    (window.innerWidth - INSET - cardRect.left) / scale - TASK_POCKET_WIDTH;
  if (maxLeft < minLeft) return null;
  return {
    ...position,
    left: Math.max(minLeft, Math.min(position.left, maxLeft)),
  };
}

/** Whether a board frame shows in this window right now. */
export function boardFrameOnScreen(frameId: string): boolean {
  for (const board of document.querySelectorAll<HTMLElement>(
    "[data-task-board]",
  )) {
    if (board.dataset.taskBoard !== frameId) continue;
    const rect = board.getBoundingClientRect();
    if (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.right > 0 &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.top < window.innerHeight
    ) {
      return true;
    }
  }
  return false;
}
